/**
 * FahrzeugePage — Fahrzeugverwaltung (Fuhrpark)
 * DESIGN: Koop-Red Akzent, professionelle Karten-Darstellung
 * Zeigt alle Fahrzeuge mit Status, Details und Verwaltungsfunktionen
 */

import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'wouter';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { useRentsoft } from '@/hooks/useRentsoft';
import { buildFahrzeugQRUrl } from '@/lib/qr-fahrzeug';
import jsPDF from 'jspdf';
import QRCode from 'qrcode';
import FahrzeugDetailDrawer from '@/components/fahrzeug/FahrzeugDetailDrawer';
import {
  Car, Plus, Search, X, Edit3, Trash2,
  Calendar, CreditCard, Gauge, Wrench, AlertTriangle,
  CheckCircle2, Clock, Package, Save, Loader2,
  RefreshCw, ExternalLink, QrCode, History, BarChart2, TrendingUp,
} from 'lucide-react';
import { FahrzeugLabelGroup, deriveLabels } from '@/components/fahrzeug/FahrzeugLabel';
import type { FahrzeugLabelTyp } from '@/components/fahrzeug/FahrzeugLabel';
import { FahrzeugeTimeline } from '@/components/fahrzeug/FahrzeugeTimeline';
import { berechneAuslastungsQuote } from '@/lib/fahrzeug-auslastung';

const KOOP_RED = '#C41E3A';

interface Fahrzeug {
  id: string;
  marke: string;
  modell: string;
  variante?: string;
  farbe?: string;
  kennzeichen?: string;
  fin?: string;
  erstzulassung?: string;
  kaufpreis_brutto?: number;
  finanzierungsart?: string;
  finanzierungspartner?: string;
  monatliche_rate?: number;
  versicherung?: string;
  versicherungsnummer?: string;
  km_aktuell?: number;
  status: string;
  betrieb: string;
  kategorie: string;
  mietklasse?: string;
  tagessatz_netto?: number;
  liefertermin?: string;
  notizen?: string;
  bild_url?: string;
  created_at: string;
  verkauft_am?: string;
  verkauft_preis_brutto?: number;
  verkauft_an?: string;
  laufzeitende?: string;
  zurueckgegeben_am?: string;
  hu_naechste?: string;
  anschaffungsdatum?: string;
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: React.ReactNode }> = {
  bestellt: { label: 'Bestellt', color: '#F59E0B', bg: '#FEF3C7', icon: <Clock className="w-3.5 h-3.5" /> },
  aktiv: { label: 'Aktiv', color: '#10B981', bg: '#D1FAE5', icon: <CheckCircle2 className="w-3.5 h-3.5" /> },
  werkstatt: { label: 'Werkstatt', color: '#EF4444', bg: '#FEE2E2', icon: <Wrench className="w-3.5 h-3.5" /> },
  reserviert: { label: 'Reserviert', color: '#6366F1', bg: '#E0E7FF', icon: <Calendar className="w-3.5 h-3.5" /> },
  verkauft: { label: 'Verkauft', color: '#6B7280', bg: '#F3F4F6', icon: <Package className="w-3.5 h-3.5" /> },
  stillgelegt: { label: 'Stillgelegt', color: '#9CA3AF', bg: '#F9FAFB', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
};

const KATEGORIE_LABELS: Record<string, string> = {
  pkw: 'PKW', transporter: 'Transporter', anhaenger: 'Anhänger', motorrad: 'Motorrad', sonstige: 'Sonstige',
};

const EMPTY_FORM: Partial<Fahrzeug> = {
  marke: '', modell: '', variante: '', farbe: '', kennzeichen: '', fin: '',
  kaufpreis_brutto: undefined, finanzierungsart: '', finanzierungspartner: '',
  status: 'aktiv', betrieb: 'lcs_mietwagen', kategorie: 'pkw',
  notizen: '', mietklasse: '', tagessatz_netto: undefined,
};

type MainTab = 'liste' | 'timeline';

interface FahrzeugKPI {
  auslastung_30d: number;
  umsatz_ytd: number;
  aktuell_vermietet_bis: string | null;
}

export default function FahrzeugePage() {
  const [fahrzeuge, setFahrzeuge] = useState<Fahrzeug[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('alle');
  const [activeTab, setActiveTab] = useState<MainTab>('liste');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<Partial<Fahrzeug>>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [kpiMap, setKpiMap] = useState<Record<string, FahrzeugKPI>>({});
  const [selectedFahrzeug, setSelectedFahrzeug] = useState<Fahrzeug | null>(null);
  const rentsoft = useRentsoft();
  const [, setLocation] = useLocation();

  const syncRentsoft = async () => {
    setSyncing(true);
    try {
      const vehicles = await rentsoft.fetchVehicles();
      if (vehicles && vehicles.length > 0) {
        let syncCount = 0;
        for (const rv of vehicles) {
          // Check if vehicle already exists by kennzeichen
          const existing = fahrzeuge.find(f => 
            f.kennzeichen && rv.kennzeichen && 
            f.kennzeichen.replace(/\s+/g, '').toLowerCase() === rv.kennzeichen.replace(/\s+/g, '').toLowerCase()
          );
          if (existing) {
            // Update existing vehicle with Rentsoft data
            await supabase.from('fahrzeuge').update({
              km_aktuell: rv.km ? parseInt(rv.km.replace(/\./g, '')) || existing.km_aktuell : existing.km_aktuell,
              fin: rv.fin || existing.fin,
              rentsoft_id: rv.id,
              rentsoft_last_sync: new Date().toISOString(),
            }).eq('id', existing.id);
            syncCount++;
          } else {
            // Create new vehicle from Rentsoft
            const nameParts = rv.name.split(' ');
            await supabase.from('fahrzeuge').insert({
              marke: nameParts[0] || rv.name,
              modell: nameParts.slice(1).join(' ') || '',
              kennzeichen: rv.kennzeichen || null,
              fin: rv.fin || null,
              km_aktuell: rv.km ? parseInt(rv.km.replace(/\./g, '')) || null : null,
              status: 'aktiv',
              betrieb: 'lcs_mietwagen',
              kategorie: rv.kategorie?.toLowerCase().includes('transport') ? 'transporter' : 'pkw',
              rentsoft_id: rv.id,
              rentsoft_last_sync: new Date().toISOString(),
            });
            syncCount++;
          }
        }
        toast.success(`Rentsoft-Sync: ${syncCount} Fahrzeuge synchronisiert (${vehicles.length} in Rentsoft)`);
        fetchFahrzeuge();
      } else {
        toast.info('Keine Fahrzeuge in Rentsoft gefunden');
      }
    } catch (e: any) {
      toast.error(`Rentsoft-Sync fehlgeschlagen: ${e.message}`);
    } finally {
      setSyncing(false);
    }
  };

  const fetchFahrzeuge = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('fahrzeuge')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) {
      toast.error('Fehler beim Laden der Fahrzeuge');
      console.error(error);
    } else {
      setFahrzeuge(data || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchFahrzeuge(); }, [fetchFahrzeuge]);

  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from('v_fahrzeug_vermietungs_stats')
        .select('fahrzeug_id, tage_vermietet_30d, umsatz_ytd, aktuell_vermietet_bis');
      if (!data) return;
      const map: Record<string, FahrzeugKPI> = {};
      for (const row of data) {
        const tage30 = (row as any).tage_vermietet_30d ?? 0;
        map[(row as any).fahrzeug_id] = {
          auslastung_30d: berechneAuslastungsQuote(tage30, 30),
          umsatz_ytd: (row as any).umsatz_ytd ?? 0,
          aktuell_vermietet_bis: (row as any).aktuell_vermietet_bis ?? null,
        };
      }
      setKpiMap(map);
    })();
  }, []);

  const handleSave = async () => {
    if (!form.marke || !form.modell) {
      toast.error('Marke und Modell sind Pflichtfelder');
      return;
    }
    setSaving(true);
    const payload = {
      marke: form.marke,
      modell: form.modell,
      variante: form.variante || null,
      farbe: form.farbe || null,
      kennzeichen: form.kennzeichen || null,
      fin: form.fin || null,
      kaufpreis_brutto: form.kaufpreis_brutto || null,
      finanzierungsart: form.finanzierungsart || null,
      finanzierungspartner: form.finanzierungspartner || null,
      status: form.status || 'aktiv',
      betrieb: form.betrieb || 'lcs_mietwagen',
      kategorie: form.kategorie || 'pkw',
      mietklasse: form.mietklasse || null,
      tagessatz_netto: form.tagessatz_netto || null,
      liefertermin: form.liefertermin || null,
      notizen: form.notizen || null,
      km_aktuell: form.km_aktuell || null,
      hu_naechste: form.hu_naechste || null,
      erstzulassung: form.erstzulassung || null,
      verkauft_am: form.verkauft_am || null,
      verkauft_preis_brutto: form.verkauft_preis_brutto || null,
      verkauft_an: form.verkauft_an || null,
    };

    if (editingId) {
      const { error } = await supabase.from('fahrzeuge').update(payload).eq('id', editingId);
      if (error) toast.error('Fehler beim Aktualisieren');
      else { toast.success('Fahrzeug aktualisiert'); setShowForm(false); setEditingId(null); }
    } else {
      const { error } = await supabase.from('fahrzeuge').insert(payload);
      if (error) toast.error('Fehler beim Anlegen');
      else { toast.success('Fahrzeug angelegt'); setShowForm(false); }
    }
    setForm(EMPTY_FORM);
    setSaving(false);
    fetchFahrzeuge();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Fahrzeug wirklich löschen?')) return;
    const { error } = await supabase.from('fahrzeuge').delete().eq('id', id);
    if (error) toast.error('Fehler beim Löschen');
    else { toast.success('Fahrzeug gelöscht'); fetchFahrzeuge(); }
  };

  const downloadFahrzeugQR = async (fz: Fahrzeug) => {
    try {
      const qrUrl = buildFahrzeugQRUrl(fz.id);
      const dataUrl = await QRCode.toDataURL(qrUrl, {
        errorCorrectionLevel: 'M',
        margin: 1,
        width: 512,
      });
      const doc = new jsPDF({ unit: 'mm', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const qrSize = 110;
      const qrX = (pageWidth - qrSize) / 2;

      doc.setFontSize(18);
      doc.text('Fahrzeug-Schlüsselkasten', pageWidth / 2, 25, { align: 'center' });

      doc.setFontSize(14);
      const titleParts = [fz.marke, fz.modell, fz.variante].filter(Boolean).join(' ');
      doc.text(titleParts, pageWidth / 2, 35, { align: 'center' });

      if (fz.kennzeichen) {
        doc.setFontSize(22);
        doc.text(fz.kennzeichen, pageWidth / 2, 48, { align: 'center' });
      }

      doc.addImage(dataUrl, 'PNG', qrX, 60, qrSize, qrSize);

      doc.setFontSize(10);
      doc.text(qrUrl, pageWidth / 2, 60 + qrSize + 8, { align: 'center' });

      doc.setFontSize(11);
      doc.text(
        'Scanne diesen Code mit der Gutachter-Koop-App, um deine Fahrt zu starten.',
        pageWidth / 2,
        60 + qrSize + 20,
        { align: 'center', maxWidth: pageWidth - 40 },
      );

      const safeName = [fz.kennzeichen || fz.id, fz.marke, fz.modell]
        .filter(Boolean)
        .join('_')
        .replace(/[^A-Za-z0-9_\-]/g, '');
      doc.save(`fahrzeug-qr-${safeName || fz.id}.pdf`);
      toast.success('QR-PDF heruntergeladen');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Unbekannter Fehler';
      toast.error(`QR-PDF konnte nicht erzeugt werden: ${message}`);
    }
  };

  const handleEdit = (fz: Fahrzeug) => {
    setForm(fz);
    setEditingId(fz.id);
    setShowForm(true);
  };

  const filtered = fahrzeuge.filter(fz => {
    const matchesSearch = search === '' || 
      `${fz.marke} ${fz.modell} ${fz.variante || ''} ${fz.kennzeichen || ''} ${fz.fin || ''}`.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'alle' || fz.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusCounts = fahrzeuge.reduce((acc, fz) => {
    acc[fz.status] = (acc[fz.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const formatCurrency = (val?: number) => val ? `${val.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €` : '—';

  return (
    <div className="p-4 lg:p-6 space-y-4 h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(196,30,58,0.06)' }}>
            <Car className="w-5 h-5" style={{ color: KOOP_RED }} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-900">Fahrzeugverwaltung</h1>
            <p className="text-xs text-gray-400">{fahrzeuge.length} Fahrzeuge im Fuhrpark</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={syncRentsoft} disabled={syncing}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sync...' : 'Rentsoft Sync'}
          </button>
          <a href="https://app.v2.rentsoft.de" target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-all">
            <ExternalLink className="w-3.5 h-3.5" /> Rentsoft
          </a>
          <button onClick={() => { setForm(EMPTY_FORM); setEditingId(null); setShowForm(true); }}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white transition-all hover:scale-105 active:scale-95"
            style={{ backgroundColor: KOOP_RED }}>
            <Plus className="w-3.5 h-3.5" /> Fahrzeug anlegen
          </button>
        </div>
      </div>

      {/* Tab-Navigation: Liste / Timeline */}
      <div className="flex items-center gap-1 border-b border-gray-100 -mb-1">
        <button
          onClick={() => setActiveTab('liste')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeTab === 'liste'
              ? 'border-red-600 text-red-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          style={{ borderBottomColor: activeTab === 'liste' ? KOOP_RED : undefined }}>
          <Car className="w-3.5 h-3.5" /> Fahrzeuge
        </button>
        <button
          onClick={() => setActiveTab('timeline')}
          className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-semibold border-b-2 transition-all ${
            activeTab === 'timeline'
              ? 'border-red-600 text-red-700'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
          style={{ borderBottomColor: activeTab === 'timeline' ? KOOP_RED : undefined }}>
          <History className="w-3.5 h-3.5" /> Timeline
        </button>
      </div>

      {activeTab === 'timeline' ? (
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
            </div>
          ) : (
            <FahrzeugeTimeline fahrzeuge={fahrzeuge} />
          )}
        </div>
      ) : (
        <>
      {/* Status Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2">
        {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
          <button key={key}
            onClick={() => setStatusFilter(statusFilter === key ? 'alle' : key)}
            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all ${
              statusFilter === key ? 'ring-2 ring-offset-1' : 'hover:bg-gray-50'
            }`}
            style={{
              borderColor: statusFilter === key ? cfg.color : '#e5e7eb',
              backgroundColor: statusFilter === key ? cfg.bg : 'white',
              outlineColor: statusFilter === key ? cfg.color : undefined,
            }}>
            <div className="flex items-center justify-center" style={{ color: cfg.color }}>
              {cfg.icon}
            </div>
            <div className="text-left">
              <div className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{cfg.label}</div>
              <div className="text-sm font-bold text-gray-900">{statusCounts[key] || 0}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Marke, Modell, Kennzeichen suchen..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 focus:border-red-300 transition-all" />
        </div>
        {statusFilter !== 'alle' && (
          <button onClick={() => setStatusFilter('alle')}
            className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors">
            <X className="w-3 h-3" /> Filter zurücksetzen
          </button>
        )}
      </div>

      {/* Fahrzeug-Liste */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-300" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Car className="w-12 h-12 text-gray-200 mx-auto mb-3" />
            <p className="text-sm text-gray-400">Keine Fahrzeuge gefunden</p>
          </div>
        ) : (
         <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map(fz => {
              const sc = STATUS_CONFIG[fz.status] || STATUS_CONFIG.aktiv;
              const kpi = kpiMap[fz.id];
              return (
                <div key={fz.id}
                  onClick={() => setSelectedFahrzeug(fz)}
                  className="bg-white rounded-xl border border-gray-100 p-4 hover:shadow-md hover:border-gray-200 transition-all cursor-pointer group"
                  style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-bold text-gray-900 truncate">{fz.marke} {fz.modell}</h3>
                      {fz.variante && <p className="text-xs text-gray-400">{fz.variante}</p>}
                    </div>
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold"
                      style={{ backgroundColor: sc.bg, color: sc.color }}>
                      {sc.icon} {sc.label}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {fz.kennzeichen && (
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="inline-block px-2 py-0.5 bg-gray-100 rounded font-mono font-bold text-gray-700">{fz.kennzeichen}</span>
                      </div>
                    )}
                    {fz.farbe && (
                      <p className="text-[11px] text-gray-400">Farbe: {fz.farbe}</p>
                    )}
                    {fz.kaufpreis_brutto && (
                      <p className="text-[11px] text-gray-400">
                        <CreditCard className="w-3 h-3 inline mr-1" />
                        {formatCurrency(fz.kaufpreis_brutto)}
                        {fz.finanzierungspartner && ` · ${fz.finanzierungspartner}`}
                      </p>
                    )}
                    {fz.liefertermin && fz.status === 'bestellt' && (
                      <p className="text-[11px] text-amber-600 font-medium">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        Liefertermin: {new Date(fz.liefertermin).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}
                      </p>
                    )}
                    {fz.km_aktuell !== null && fz.km_aktuell !== undefined && (
                      <p className="text-[11px] text-gray-400">
                        <Gauge className="w-3 h-3 inline mr-1" />
                        {fz.km_aktuell.toLocaleString('de-DE')} km
                      </p>
                    )}
                    {/* KPI-Zeilen aus v_fahrzeug_vermietungs_stats */}
                    {kpi && (
                      <div className="space-y-1 pt-1">
                        {kpi.aktuell_vermietet_bis && (
                          <p className="text-[11px] font-medium text-blue-700">
                            Vermietet bis {new Date(kpi.aktuell_vermietet_bis).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                          </p>
                        )}
                        <div className="flex items-center gap-3">
                          <span className="text-[10px] text-gray-400 flex items-center gap-1">
                            <BarChart2 className="w-3 h-3" />
                            {kpi.auslastung_30d} % (30d)
                          </span>
                          {kpi.umsatz_ytd > 0 && (
                            <span className="text-[10px] text-gray-400 flex items-center gap-1">
                              <TrendingUp className="w-3 h-3" />
                              {kpi.umsatz_ytd.toLocaleString('de-DE', { minimumFractionDigits: 0 })} € YTD
                            </span>
                          )}
                        </div>
                        {kpi.auslastung_30d > 0 && (
                          <div className="w-full bg-gray-100 rounded-full h-1">
                            <div
                              className="h-1 rounded-full"
                              style={{ width: `${Math.min(kpi.auslastung_30d, 100)}%`, backgroundColor: KOOP_RED }}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {/* FahrzeugLabels */}
                    {(() => {
                      const labels = deriveLabels(fz) as FahrzeugLabelTyp[];
                      return labels.length > 0 ? (
                        <div className="pt-1">
                          <FahrzeugLabelGroup labels={labels} max={2} />
                        </div>
                      ) : null;
                    })()}
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-50">
                    <span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">
                      {KATEGORIE_LABELS[fz.kategorie] || fz.kategorie}
                    </span>
                    <div className="flex items-center gap-1 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={(e) => { e.stopPropagation(); handleEdit(fz); }}
                        className="w-11 h-11 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                        <Edit3 className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-gray-400" />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(fz.id); }}
                        className="w-11 h-11 sm:w-7 sm:h-7 rounded-lg flex items-center justify-center hover:bg-red-50 transition-colors">
                        <Trash2 className="w-4 h-4 sm:w-3.5 sm:h-3.5 text-red-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
        </>
      )}

      {/* Detail-Drawer (Issue #185) */}
      <FahrzeugDetailDrawer
        fahrzeug={selectedFahrzeug}
        open={!!selectedFahrzeug}
        onClose={() => setSelectedFahrzeug(null)}
        onChanged={fetchFahrzeuge}
      />

      {/* Formular-Modal */}
      {showForm && (
        <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" onClick={() => { setShowForm(false); setEditingId(null); }}>
          <div className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-lg mx-0 sm:mx-4 sm:h-auto max-h-[85vh] overflow-auto" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-sm font-bold text-gray-900">{editingId ? 'Fahrzeug bearbeiten' : 'Neues Fahrzeug anlegen'}</h3>
              <button onClick={() => { setShowForm(false); setEditingId(null); }}
                className="w-11 h-11 sm:w-8 sm:h-8 rounded-lg flex items-center justify-center hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 sm:w-4 sm:h-4 text-gray-400" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Marke *</label>
                  <input type="text" value={form.marke || ''} onChange={e => setForm({ ...form, marke: e.target.value })}
                    placeholder="z.B. BMW" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Modell *</label>
                  <input type="text" value={form.modell || ''} onChange={e => setForm({ ...form, modell: e.target.value })}
                    placeholder="z.B. iX1" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Variante</label>
                  <input type="text" value={form.variante || ''} onChange={e => setForm({ ...form, variante: e.target.value })}
                    placeholder="z.B. eDrive20" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Farbe</label>
                  <input type="text" value={form.farbe || ''} onChange={e => setForm({ ...form, farbe: e.target.value })}
                    placeholder="z.B. Saphirschwarz" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Kennzeichen</label>
                  <input type="text" value={form.kennzeichen || ''} onChange={e => setForm({ ...form, kennzeichen: e.target.value })}
                    placeholder="UN-XX 1234" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">FIN</label>
                  <input type="text" value={form.fin || ''} onChange={e => setForm({ ...form, fin: e.target.value })}
                    placeholder="WBAXXXXXXX" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Status</label>
                  <select value={form.status || 'aktiv'} onChange={e => setForm({ ...form, status: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 bg-white">
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Kategorie</label>
                  <select value={form.kategorie || 'pkw'} onChange={e => setForm({ ...form, kategorie: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 bg-white">
                    {Object.entries(KATEGORIE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Kaufpreis brutto (€)</label>
                  <input type="number" step="0.01" value={form.kaufpreis_brutto || ''} onChange={e => setForm({ ...form, kaufpreis_brutto: parseFloat(e.target.value) || undefined })}
                    placeholder="47310.71" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Finanzierung</label>
                  <input type="text" value={form.finanzierungsart || ''} onChange={e => setForm({ ...form, finanzierungsart: e.target.value })}
                    placeholder="Leasing / Finanzierung" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Bank</label>
                  <input type="text" value={form.finanzierungspartner || ''} onChange={e => setForm({ ...form, finanzierungspartner: e.target.value })}
                    placeholder="BMW Bank" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Liefertermin</label>
                  <input type="date" value={form.liefertermin || ''} onChange={e => setForm({ ...form, liefertermin: e.target.value })}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">KM-Stand</label>
                  <input type="number" value={form.km_aktuell || ''} onChange={e => setForm({ ...form, km_aktuell: parseInt(e.target.value) || undefined })}
                    placeholder="0" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
                <div>
                  <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Tagessatz netto (€)</label>
                  <input type="number" step="0.01" value={form.tagessatz_netto || ''} onChange={e => setForm({ ...form, tagessatz_netto: parseFloat(e.target.value) || undefined })}
                    placeholder="65.00" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                </div>
              </div>
              {form.status === 'verkauft' && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Verkauft am</label>
                      <input type="date" value={form.verkauft_am || ''} onChange={e => setForm({ ...form, verkauft_am: e.target.value })}
                        className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Verkaufspreis brutto (€)</label>
                      <input type="number" step="0.01" value={form.verkauft_preis_brutto || ''} onChange={e => setForm({ ...form, verkauft_preis_brutto: parseFloat(e.target.value) || undefined })}
                        placeholder="15000.00" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Käufer (Name / Firma)</label>
                    <input type="text" value={form.verkauft_an || ''} onChange={e => setForm({ ...form, verkauft_an: e.target.value })}
                      placeholder="Max Mustermann" className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200" />
                  </div>
                </>
              )}
              <div>
                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Notizen</label>
                <textarea value={form.notizen || ''} onChange={e => setForm({ ...form, notizen: e.target.value })}
                  rows={3} placeholder="Freitext..."
                  className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-200 resize-none" />
              </div>
              <div className="flex justify-end gap-2 pt-2 mobile-dialog-actions sm:pb-0">
                <button onClick={() => { setShowForm(false); setEditingId(null); }}
                  className="px-4 py-2 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors">
                  Abbrechen
                </button>
                <button onClick={handleSave} disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold text-white transition-all hover:scale-105 disabled:opacity-50"
                  style={{ backgroundColor: KOOP_RED }}>
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {editingId ? 'Aktualisieren' : 'Anlegen'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
